import React from "react";
import { Job } from "../../../models/Job";
import { RegisterData, shortDate } from "../../sections/desk/useRegister";
import { Leaf } from "./shell";
import { VELLUM as T, MONO, display, mono, body, SheetHead, Callout } from "./tokens";

/* A-03 · detail - what a listing carries, drawn at 1:1.

   When the register holds nothing yet there is still a listing to show. This
   one is authored, built entirely from the canonical taxonomy's real values,
   and labelled on the page as a sample so no visitor can mistake it for a
   post that exists. */
const SAMPLE = {
  title: "External Examiner, MSc Computational Neuroscience",
  company: "A UK research-intensive university",
  subject: "Computational neuroscience",
  subjectCategory: "Biological and Sport Sciences",
  studyLevels: ["MSc", "PhD"],
  academicRoles: ["External Examiner"],
  employmentType: "Contract",
  contractDuration: "Fixed 2 Years",
  careerLevel: "Senior Fellow",
  experience: "6-9 Years",
};

type Row = [string, string];

function rowsFor(job: Job | null): Row[] {
  const src = job
    ? {
        subject: job.subject || job.subjectCategory,
        studyLevels: job.studyLevels,
        academicRoles: job.academicRoles,
        employmentType: job.employmentType,
        contractDuration: job.contractDuration,
        careerLevel: job.careerLevel,
        experience: job.experience,
        closes: shortDate(job.applicationDeadline),
      }
    : {
        subject: SAMPLE.subject,
        studyLevels: SAMPLE.studyLevels,
        academicRoles: SAMPLE.academicRoles,
        employmentType: SAMPLE.employmentType,
        contractDuration: SAMPLE.contractDuration,
        careerLevel: SAMPLE.careerLevel,
        experience: SAMPLE.experience,
        closes: null as string | null,
      };

  const out: Row[] = [];
  const push = (k: string, v?: string | string[] | null) => {
    if (!v) return;
    const s = Array.isArray(v) ? v.join(" · ") : v;
    if (s && s !== "None") out.push([k, s]);
  };

  push("Subfield", src.subject);
  push("Study levels", src.studyLevels);
  push("Academic role", src.academicRoles);
  push("Employment", src.employmentType);
  push("Contract", src.contractDuration);
  push("Career level", src.careerLevel);
  push("Experience", src.experience);
  push("Closes", src.closes);
  return out;
}

/* This leaf exists to show the academic fields. A live post that carries none
   of them proves the opposite of the argument, so it is only used when it
   actually holds enough of them; otherwise the labelled sample is the honest
   illustration. */
function academicDetail(job: Job): number {
  let n = 0;
  if (job.subject || job.subjectCategory) n++;
  if (job.studyLevels && job.studyLevels.length > 0) n++;
  if (job.academicRoles && job.academicRoles.length > 0) n++;
  if (job.contractDuration) n++;
  return n;
}

const NOTES: [string, string][] = [
  [
    "Why subfield",
    "“Computational neuroscience”, not “Science”. It is the level a working academic actually searches at, and the level the register indexes.",
  ],
  [
    "Why study level",
    "An MSc examiner and a PhD examiner are different appointments. The register keeps them apart.",
  ],
  [
    "Why the role",
    "External examiner, panel member, end point assessor. Named work, not a generic vacancy.",
  ],
];

export const LeafAnatomy: React.FC<{ register: RegisterData }> = ({ register }) => {
  const candidate = register.jobs.find((j) => academicDetail(j) >= 2);
  const job = candidate || null;
  const rows = rowsFor(job);
  const title = job ? job.title : SAMPLE.title;
  const institution = job ? (job.company || "").trim() : SAMPLE.company;

  return (
    <Leaf
      code="§ A-03 · detail"
      measure={
        <>
          <SheetHead t={T} code="1:1">
            What a listing carries.
          </SheetHead>

          <p style={{ ...body(T), fontSize: 17, maxWidth: 600, marginBottom: 30 }}>
            A general job board files academic work under “Education” and stops there. Every entry
            in this register is filed the way the work is actually described - by subfield, by the
            levels it is taught or examined at, and by the role being filled.
          </p>

          <div style={{ border: `1.5px solid ${T.ink}`, padding: 26 }}>
            {!job && <div style={{ ...mono(T, T.accent), marginBottom: 14 }}>Sample entry · not a live post</div>}
            <div style={display(T, "clamp(20px, 2.5vw, 27px)")}>{title}</div>
            <div style={{ ...body(T), fontSize: 14.5, marginTop: 8, marginBottom: 20 }}>{institution}</div>

            <dl style={{ margin: 0 }}>
              {rows.map(([k, v]) => (
                <div
                  key={k}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "132px minmax(0,1fr)",
                    gap: 14,
                    alignItems: "baseline",
                    padding: "10px 0",
                    borderTop: `1px solid ${T.rule}`,
                  }}
                >
                  <dt style={{ ...mono(T), fontSize: 10 }}>{k}</dt>
                  <dd style={{ margin: 0, fontFamily: MONO, fontSize: 13.5, color: T.ink }}>{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </>
      }
      margin={
        <>
          {NOTES.map(([head, note], i) => (
            <div
              key={head}
              style={{
                display: "grid",
                gridTemplateColumns: "30px minmax(0,1fr)",
                gap: 12,
                borderTop: `1px solid ${T.rule}`,
                padding: "14px 0",
              }}
            >
              <Callout t={T}>{String.fromCharCode(65 + i)}</Callout>
              <span>
                <span style={{ ...mono(T, T.ink), display: "block", marginBottom: 6 }}>{head}</span>
                <p style={{ ...body(T), fontSize: 13.5 }}>{note}</p>
              </span>
            </div>
          ))}
        </>
      }
    />
  );
};
